import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import { OPERATOR_ACTION_ROLE, effectiveImageRole, roleAtLeast } from "../image/image-ui.js";
import type { PersonaSummary } from "../../../api/types.js";
import { PROJECT_ACTION_KEY, RE_ANALYSIS_APPROVED_STATES, RE_ERROR_TEXT, RE_IN_FLIGHT_STATES, RE_REASON_TEXT, RE_STATE_LABEL, reSourceTooLong, reSourceTooLongText, type ReProjectViewDto, type ReRevisionDto } from "./re-api.js";
import type { SourceVideoSummary } from "../../../api/types.js";
import { ReAnalysisView } from "./ReAnalysisView.js";
import { ReProposals } from "./ReProposals.js";
import { ReReuse } from "./ReReuse.js";

const POLL_MS = 5000;
const STATUS_TEXT: Record<ReRevisionDto["status"], string> = { requested: "sending", awaiting_callback: "Grok is analyzing", ready: "ready", failed: "failed", timed_out: "timed out", approved: "approved" };
const fmt = (iso: string | null): string => (iso === null ? "—" : new Date(iso).toLocaleString());

/**
 * FIX-011 / RE Stage 4 - one RE project: the source video beside Grok's full analysis, correction requests (every one a new version, nothing overwritten), the version history,
 * and approval 1 (locks the chosen version). Reads only what Emma OS already stored; the only writes are the stage-2/3 actions behind the buttons.
 */
export function ReProject({ tenantId }: { readonly tenantId: string }) {
  const { projectId = "" } = useParams();
  const { csrfToken, user, memberships } = useAuth();
  const canAct = roleAtLeast(effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships, tenantId }), OPERATOR_ACTION_ROLE);
  const [view, setView] = useState<ReProjectViewDto | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // FIX-064 RE UI lock (owner t642, 2026-10-22): in-flight actions are tracked PER KEY, not one project-wide `busy` flag — "project" (analysis / approval /
  // correction / first proposals), "more-proposals", and `proposal:<id>` for each proposal card. A pending request disables only its own key's buttons; the ref is a
  // synchronous same-tick guard against a double click before React re-renders. The lock is released as soon as the POST settles — the reload after it runs unlocked.
  const pendingRef = useRef<Set<string>>(new Set());
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const isPending = useCallback((key: string): boolean => pendingKeys.has(key), [pendingKeys]);
  const busy = pendingKeys.has(PROJECT_ACTION_KEY);
  const [personaName, setPersonaName] = useState<string>("the selected persona");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [correction, setCorrection] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sourceSeconds, setSourceSeconds] = useState<number | null | undefined>(undefined); // undefined = not loaded yet
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const errorText = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    const reason = (body as { reason?: string } | null)?.reason;
    return (reason !== undefined ? RE_REASON_TEXT[reason] : undefined) ?? (body?.error !== undefined ? RE_ERROR_TEXT[body.error] : undefined) ?? body?.message ?? fallback;
  };

  const load = useCallback(async () => {
    try {
      const v = await api.get<ReProjectViewDto>(`/re/${tenantId}/projects/${encodeURIComponent(projectId)}`);
      if (mounted.current) { setView(v); setNotFound(false); }
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else setError(errorText(e, "Could not load the project."));
    }
  }, [tenantId, projectId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (view === null || !(RE_IN_FLIGHT_STATES.includes(view.project.state) || view.project.activeRequestId !== null)) return undefined; // any Grok request in flight (analysis, proposals, more proposals)
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(id);
  }, [view, load]);
  useEffect(() => {
    if (view === null) return;
    void api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`).then((r) => {
      const p = r.personas.find((x) => x.personaId === view.project.personaId);
      if (p !== undefined && mounted.current) setPersonaName(p.displayName);
    }).catch(() => undefined);
  }, [tenantId, view?.project.personaId]);

  const sourceVideoId = view?.project.sourceVideoId ?? null;
  useEffect(() => {
    if (sourceVideoId === null) return;
    void api.get<SourceVideoSummary>(`/videos/${tenantId}/source-videos/${encodeURIComponent(sourceVideoId)}`).then((r) => { if (mounted.current) setSourceSeconds(r.durationSeconds); }).catch(() => undefined);
  }, [tenantId, sourceVideoId]);

  const act = async (path: string, body?: unknown, key: string = PROJECT_ACTION_KEY): Promise<boolean> => {
    if (pendingRef.current.has(key)) return false; // same key already in flight (e.g. a double click in the same tick): no second POST
    pendingRef.current.add(key);
    setPendingKeys(new Set(pendingRef.current));
    setError(null);
    let ok: boolean;
    try {
      await api.post(`/re/${tenantId}/projects/${encodeURIComponent(projectId)}${path}`, csrfToken, body ?? {});
      ok = true;
    } catch (e) {
      if (mounted.current) setError(errorText(e, "That action failed."));
      ok = false;
    } finally {
      pendingRef.current.delete(key);
      if (mounted.current) setPendingKeys(new Set(pendingRef.current));
    }
    await load(); // unlocked: other buttons are never held by the reload
    return ok;
  };

  if (notFound) return <p data-testid="re-not-found">This project was not found (or it is not yours). <Link to="/generate/create/re/projects">My RE projects</Link></p>;
  if (view === null) return <p className="muted">{error ?? "Loading…"}</p>;

  const { project, analysisRevisions: revisions } = view;
  const approved = revisions.find((r) => r.status === "approved") ?? null;
  const current = revisions.find((r) => r.id === project.currentAnalysisRevisionId) ?? null;
  const latestWithAnalysis = [...revisions].reverse().find((r) => r.analysis !== null) ?? null;
  const shown = revisions.find((r) => r.id === selectedId) ?? approved ?? current ?? latestWithAnalysis;
  const inFlight = RE_IN_FLIGHT_STATES.includes(project.state);
  // The 30-second RE rule also covers a project created before it existed: its analysis cannot be (re)started - the server refuses it as well.
  const sourceBlocked = sourceSeconds !== undefined && reSourceTooLong(sourceSeconds);
  const pending = [...revisions].reverse().find((r) => r.status === "requested" || r.status === "awaiting_callback") ?? null;
  const lastFailed = [...revisions].reverse().find((r) => r.status === "failed" || r.status === "timed_out") ?? null;
  const locked = approved !== null && RE_ANALYSIS_APPROVED_STATES.includes(project.state);
  const readyToApprove = project.state === "ANALYSIS_READY" && current !== null && current.status === "ready";

  return (
    <section data-testid="re-project">
      <p><Link to="/generate/create/re/projects">← My RE projects</Link></p>
      <h3>RE project — {personaName}</h3>
      <p data-testid="re-state"><strong>{RE_STATE_LABEL[project.state]}</strong> <span className="muted">· started {fmt(project.createdAt)}</span></p>
      {error !== null ? <div className="error-banner" role="alert" data-testid="re-error">{error}</div> : null}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "0 0 280px" }}>
          <h4 style={{ marginTop: 0 }}>Source video</h4>
          {project.sourceVideoId !== null ? (
            <video data-testid="re-source-video" controls preload="metadata" style={{ width: "100%", maxHeight: 460, background: "#000" }} src={`/videos/${tenantId}/source-videos/${project.sourceVideoId}/preview`} />
          ) : <p className="muted">No source video.</p>}
        </div>

        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
          {sourceBlocked && !locked && !readyToApprove ? (
            <div className="card" data-testid="re-source-too-long">
              <p style={{ margin: 0 }}><strong>This source video cannot be analyzed.</strong> {reSourceTooLongText(sourceSeconds)}</p>
            </div>
          ) : null}

          {(project.state === "SOURCE_READY" || project.state === "CREATED") && !sourceBlocked ? (
            <div className="card" data-testid="re-start-card">
              <p>The source video is ready. Send it to Grok for a full analysis (this takes a few minutes).</p>
              {canAct ? <button type="button" data-testid="re-start-analysis" disabled={busy || project.state === "CREATED"} onClick={() => void act("/analysis")}>Analyze</button> : <p className="muted">Your role cannot start an analysis.</p>}
            </div>
          ) : null}

          {inFlight ? (
            <div className="card" data-testid="re-in-flight">
              <p style={{ marginTop: 0 }}>{project.state === "ANALYSIS_REQUESTED" ? "Sending the video to Grok…" : "Grok is analyzing the video…"}</p>
              <p className="muted" style={{ marginBottom: 0 }}>{pending !== null ? `Version ${pending.revisionNo}${pending.userCorrectionText !== null ? " (correction)" : ""} · started ${fmt(pending.createdAt)} · gives up at ${fmt(pending.deadlineAt)}. ` : ""}This page updates by itself.</p>
            </div>
          ) : null}

          {(project.state === "ANALYSIS_FAILED" || project.state === "ANALYSIS_TIMED_OUT") ? (
            <div className="card" data-testid="re-failed">
              <p style={{ marginTop: 0 }}><strong>{project.state === "ANALYSIS_TIMED_OUT" ? "The analysis did not come back in time." : "The analysis failed."}</strong>{lastFailed?.errorCode != null ? ` (${lastFailed.errorCode})` : ""}</p>
              {lastFailed?.errorMessage != null ? <p className="muted">{lastFailed.errorMessage}</p> : null}
              <p className="muted">Nothing is retried automatically. {lastFailed?.userCorrectionText != null ? "Your correction request is kept and will be sent again." : ""}</p>
              {canAct && !sourceBlocked ? <button type="button" data-testid="re-retry" disabled={busy} onClick={() => void act("/analysis")}>Try again</button> : null}
            </div>
          ) : null}

          {locked && approved !== null ? (
            <div className="card" data-testid="re-approved-banner">
              <strong>Approved — version {approved.revisionNo} is locked.</strong> <span className="muted">Approved {fmt(approved.approvedAt)}. It can no longer be changed. Choose your proposals below.</span>
            </div>
          ) : null}

          {readyToApprove && current !== null ? (
            <div className="card" data-testid="re-actions">
              <h4 style={{ marginTop: 0 }}>Review version {current.revisionNo}</h4>
              {canAct ? (
                <>
                  {!confirming ? (
                    <button type="button" data-testid="re-approve" disabled={busy} onClick={() => setConfirming(true)}>Approve this analysis</button>
                  ) : (
                    <p data-testid="re-approve-confirm">
                      Approving locks version {current.revisionNo} for good and unlocks the next step.{" "}
                      <button type="button" data-testid="re-approve-confirm-yes" disabled={busy} onClick={() => void act(`/analysis/${encodeURIComponent(current.id)}/approve`).then(() => { setConfirming(false); setSelectedId(null); })}>Yes, approve</button>{" "}
                      <button type="button" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
                    </p>
                  )}
                  <hr />
                  <p className="muted">Check the analysis against the video. If something is wrong, describe the correction (about the analysis only); Grok will analyze again and the new version appears here.</p>
                  <textarea data-testid="re-correction-text" rows={3} maxLength={4000} style={{ width: "100%" }} placeholder="e.g. She is not smiling at 8 seconds; the nametag says ANNA." value={correction} onChange={(e) => setCorrection(e.target.value)} />
                  <p>
                    <button type="button" data-testid="re-request-correction" disabled={busy || correction.trim() === ""} onClick={() => void act("/analysis", { correctionText: correction.trim() }).then((ok) => { if (ok) { setCorrection(""); setSelectedId(null); } })}>Request correction</button>
                  </p>
                </>
              ) : <p className="muted">Your role can read this analysis but not change it.</p>}
            </div>
          ) : null}
        </div>
      </div>

      {approved !== null && RE_ANALYSIS_APPROVED_STATES.includes(project.state) ? <ReProposals view={view} tenantId={tenantId} canAct={canAct} isPending={isPending} act={(path, body, key) => act(path, body, key)} /> : null}
      {approved !== null && RE_ANALYSIS_APPROVED_STATES.includes(project.state) ? <ReReuse key={project.id} tenantId={tenantId} projectId={project.id} currentPersonaId={project.personaId} csrfToken={csrfToken} canAct={canAct} /> : null}

      {revisions.length > 0 ? (
        <section className="card" data-testid="re-history" style={{ marginTop: 12 }}>
          <h4 style={{ marginTop: 0 }}>Versions</h4>
          <table>
            <thead><tr><th>Version</th><th>Status</th><th>Time</th><th>Correction requested</th><th /></tr></thead>
            <tbody>
              {[...revisions].reverse().map((r) => (
                <tr key={r.id} data-testid="re-version" data-revision={r.revisionNo}>
                  <td>{r.revisionNo}{r.id === shown?.id ? " (shown)" : ""}</td>
                  <td>{STATUS_TEXT[r.status]}{r.status === "failed" && r.errorCode !== null ? ` (${r.errorCode})` : ""}</td>
                  <td>{fmt(r.usedAt ?? r.createdAt)}</td>
                  <td>{r.userCorrectionText ?? <span className="muted">—</span>}</td>
                  <td>{r.analysis !== null && r.id !== shown?.id ? <button type="button" data-testid="re-view-version" onClick={() => setSelectedId(r.id)}>View</button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {shown !== null && shown.analysis !== null ? (
        <div style={{ marginTop: 12 }}>
          <h3 data-testid="re-analysis-title">Grok's analysis — version {shown.revisionNo}{shown.status === "approved" ? " (approved)" : shown.id !== current?.id && shown.id !== approved?.id ? " (older version)" : ""}</h3>
          <ReAnalysisView analysis={shown.analysis} personaName={personaName} />
        </div>
      ) : null}
    </section>
  );
}
